import React, { useEffect, useState, useRef, createRef } from "react";
import cx from "classnames";
import styles from "../scss/AFQuestionForm.module.scss";
import style from "../scss/AppFormField.module.scss";
import { ControlType } from "../enums/ControlType";
import Input from "../../common/components/Forms/Input/Input";
import AFDisclosure, { IDisclosureOptionProps } from "./AFDisclosure";
import {
    IAnswerDisclosureProps,
    IAnswerProps,
    IAppFormQuestion
} from "../interfaces/IAppFormData";
import { QuestionType } from "../enums/QuestionType";
import AFDateInput from "./AFDateInput";
import { getDisclosureAnswersValue, isNumber } from "../shared/AppFormHelper";
import AFMultiDisclosure from "./AFMultiDisclosure";
import AFDateDropdown from "./AFDateDropdown";
import Dropdown from "../../common/components/Forms/Dropdown/Dropdown";
import AFBankAccountInput from "./AFBankAccountInput";
import AFAddressField from "./AFAddressField";
import AFTextareaField from "./AFTextareaField";
import useWindowDimensions from "../../common/hooks/useWindowDimensions";
import ValidationMessage from "../../common/components/Forms/ValidationMessage/ValidationMessage";
import useAFForm from "../hooks/useAFForm";
import AFSingleText from "./AFSingleText";

interface IAppFormFieldProps {
    question: IAppFormQuestion;
    answerChanges: (answerParam: IAnswerProps) => void;
    fieldReady: (status: boolean) => void;
    label?: string;
}

const AppFormField: React.FC<IAppFormFieldProps> = (props) => {
    const { question, answerChanges} = props;

    const { isTabletPortrait } = useWindowDimensions();

    const [value, setValue] = useState("");
    const [email, setEmail] = useState("");
    const [valueDropList, setValueDropList] = useState("");
    const [valueNumber, setValueNumber] = useState<number>();
    const [valueNumberError, setValueNumberError] = useState("");
    const [existingDisclosure, setExistingDisclosure] = useState<
        IAnswerDisclosureProps
    >();
    const [disclosure, setDisclosure] = useState<IDisclosureOptionProps>({
        label: "",
        value: ""
    });
    const [isReady, setIsReady] = useState(false);
    const [showAddress, setShowAddress] = useState(false);
    const [googlePlacesApiKey, setGooglePlacesApiKey] = useState("");

    const { validEmail } = useAFForm();

    const isFieldInvalid = (value): boolean => {
        if (!isNumber(value) && !value || isNaN(value)) return true;

        let lowerLimit = question?.rangeStart;
        let upperLimit = question?.rangeEnd;
        if (lowerLimit === undefined) lowerLimit = null;
        if (upperLimit === undefined) upperLimit = null;
        setValueNumberError("");

        if (lowerLimit !== null && upperLimit !== null) {
            if (value < lowerLimit || value > upperLimit) {
                setValueNumberError(
                    `Please provide a number between ${lowerLimit} and ${upperLimit}`
                );
                return true;
            }
        } else {
            if (lowerLimit !== null && value < lowerLimit) {
                setValueNumberError(
                    `Please provide a number higher than ${lowerLimit}`
                );
                return true;
            }
            if (upperLimit !== null && value > upperLimit) {
                setValueNumberError(
                    `Please provide a number lower than ${upperLimit}`
                );
                return true;
            }
        }
        return false;
    };

    const setDefaultValue = (): void => {
        if (question?.questionType === QuestionType.DISCLOSURE) {
            handleDisclosureInputValue(question?.disclosures);
            if (question?.disclosures?.length) {
                setExistingDisclosure({
                    ...question?.disclosures[0],
                    selected: false
                });
            }
        } else if (question?.questionType === QuestionType.DISCLOSURES) {
            // TODO: Why is this commented out? Is this still used/needed?
            // handleDisclosureInputValue(question?.disclosures);
            // if (question?.disclosures?.length) {
            //     setExistingDisclosure({
            //         ...question?.disclosures[0],
            //         selected: false
            //     });
            // }
        } else if (question.questionType === QuestionType.QUESTION_LIST) {
            switch (question.controlTypeId) {
                case ControlType.TEXTFIELD:
                case ControlType.OPENENDEDTEXT:
                case ControlType.PHONE_NUMBER:
                    handleInputTextValue(question.answer);
                    break;
                case ControlType.EMAIL_ADDRESS:
                    handleEmailValue(question.answer);
                    break;
                case ControlType.DROPLIST:
                    handleDropListValue(question.answer);
                    break;
                case ControlType.NUMBER:
                    handleInputNumberValue(parseInt(question?.answer));
                    break;
                case ControlType.RADIOBUTTON:
                    handleInputTextValue(question?.answer);
                    break;
                case ControlType.SINGLE_TEXT:
                    if (question?.answer) {
                        handleInputTextValue(question?.answer);
                    }
                default:
            }
        }
    };

    const handleInputNumberValue = (value: number): void => {
        setValueNumber(value);
        answerChanges({
            questionId: question.id,
            disclosure: [],
            answer: isFieldInvalid(value) ? "" : `${value}`
        });
    };

    const handleDropListValue = (value: string): void => {
        setValueDropList(value);
        answerChanges({
            questionId: question.id,
            disclosure: [],
            answer: value
        });
    };

    const handleInputTextValue = (value: string): void => {
        setValue(value);
        if (value) {
            answerChanges({
                questionId: question.id,
                disclosure: [],
                answer: value
            });
        }
    };

    const handleSingleText = (value: string): void => {
        setValue(value);
        answerChanges({
            questionId: question.id,
            disclosure: [],
            answer: value
        });
    };

    const handleEmailValue = (value: string): void => {
        setEmail(value);
        answerChanges({
            questionId: question.id,
            disclosure: [],
            answer: validEmail(value) ? value : ""
        });
    };

    const handleDisclosureInputValue = (disclosures: IAnswerDisclosureProps[]): void => {
        const objValue = (disclosures?.length && disclosures[0]) || {
            disclosure: "",
            disclosureId: "",
            selected: false,
            sortOrder: 0
        };
        const answer = getDisclosureAnswersValue(
            question.availableQuestionAnswers,
            objValue.disclosureId === "-1"
        );
        if (question.questionType === QuestionType.DISCLOSURE) {
            setDisclosure({
                label: objValue.disclosure,
                value: objValue.disclosureId
            });
        }
        if (!objValue.disclosureId) {
            answerChanges({
                questionId: question.id,
                answer: "",
                disclosure: []
            });
            return;
        }
        answerChanges({
            questionId: question.id,
            answer,
            disclosure: answer === "CantFind" ? [] : disclosures
        });
        return;
    };

    useEffect(() => {
        if (question?.id) {
            props.fieldReady(true);
            setIsReady(true);
            setDefaultValue();
        }
    }, [question]);

    useEffect(() => {
        setShowAddress(Boolean(window.googlePlacesApiKey));
        if (Boolean(window.googlePlacesApiKey))
            setGooglePlacesApiKey(window.googlePlacesApiKey);
    }, [window.googlePlacesApiKey]);

    const elementRef = useRef(null);
    const [elementVisible, setElementVisible] = useState(false);
    const handleElementRef = (el) => {
        elementRef.current = el;
        setElementVisible(!!el);
    };

    useEffect(() => {
        if (elementRef.current) {
            if (question.controlTypeId === ControlType.DATE_PICKER && isTabletPortrait)
                return;
            elementRef.current.focus();
        }
    }, [elementVisible]);

    if (!isReady) return <div></div>;
    return (
        <React.Fragment>
            {question.questionType === QuestionType.DISCLOSURE && (
                <div className={styles["field-occupation-input"]}>
                    <AFDisclosure
                        id={props?.label}
                        title={" "}
                        className={styles["field-occupation-input--style"]}
                        placeholder={
                            question?.placeholder || "Search Text (minimum 3 char)"
                        }
                        parameterType={`${question?.questionTypeParameter}`}
                        initialValue={disclosure}
                        onSelect={(e) => {
                            handleDisclosureInputValue(e);
                        }}
                        onBlur={() => { }}
                        hasCantFindOption={question?.availableQuestionAnswers?.some(a => a.value === "CantFind")}
                    />
                </div>
            )}
            {question.questionType === QuestionType.DISCLOSURES && (
                <div className={styles["field-occupation-input"]}>
                    <AFMultiDisclosure
                        id={props?.label}
                        title={" "}
                        className={styles["field-occupation-input--style"]}
                        placeholder={
                            question?.placeholder || "Search Text (minimum 3 char)"
                        }
                        parameterType={`${question?.questionTypeParameter}`}
                        initialValues={question?.disclosures}
                        onSelect={(e) => {
                            handleDisclosureInputValue(e);
                        }}
                        onBlur={() => { }}
                        hasCantFindOption={question?.availableQuestionAnswers?.some(a => a.value ==="CantFind")}
                    />
                </div>
            )}
            {question.controlTypeId === ControlType.TEXTFIELD && (
                <div className={style["af-text-input"]} id={`${question.id}`}>
                    <div
                        className={cx([
                            style["input-field"],
                            !!question?.unit ? style["has-label"] : ""
                        ])}>
                        <Input
                            id={props?.label}
                            name={props?.label}
                            type="text"
                            value={value || ""}
                            onChange={(e) => {
                                handleInputTextValue(e.target.value);
                            }}
                            placeholder={question?.placeholder || ""}
                            textRef={handleElementRef}></Input>
                    </div>
                    {question?.unit && <span>{question?.unit}</span>}
                </div>
            )}
            {question.controlTypeId === ControlType.SINGLE_TEXT && (
                <div className={style["af-text-input"]} id={`${question.id}`}>
                    <div
                        className={cx([
                            style["input-field"],
                            !!question?.unit ? style["has-label"] : ""
                        ])}>
                        <Input
                            id={props?.label}
                            name={props?.label}
                            type="text"
                            value={value || ""}
                            onChange={(e) => {
                                handleSingleText(e.target.value);
                            }}
                            placeholder={question?.placeholder || ""}
                            textRef={handleElementRef}></Input>
                    </div>
                </div>
            )}
            {question.controlTypeId === ControlType.OPENENDEDTEXT && (
                <div className={style["af-text-input"]} id={`${question.id}`}>
                    <div
                        className={cx([
                            style["input-field"],
                            !!question?.unit ? style["has-label"] : ""
                        ])}>
                        <Input
                            id={props?.label}
                            name={props?.label}
                            type="textarea"
                            value={value || ""}
                            onChange={(e) => {
                                handleInputTextValue(e.target.value);
                            }}
                            placeholder={question?.placeholder || ""}
                            textRef={handleElementRef}></Input>
                    </div>
                </div>
            )}
            {question.controlTypeId === ControlType.NUMBER && (
                <>
                    <div className={style["af-text-input"]} id={`${question.id}`}>
                        <div
                            className={cx([
                                style["input-field"],
                                !!question?.unit ? style["has-label"] : ""
                            ])}>
                            <Input
                                id={props?.label}
                                name={props?.label}
                                type="number"
                                hasCurrency={false}
                                value={valueNumber}
                                onChange={handleInputNumberValue}
                                placeholder={question?.placeholder}
                                textRef={handleElementRef}
                                hasError={Boolean(valueNumberError)}></Input>
                        </div>
                        {question?.unit && <span>{question?.unit}</span>}
                    </div>
                    {Boolean(valueNumberError) && (
                        <ValidationMessage>{valueNumberError}</ValidationMessage>
                    )}
                </>
            )}
            {question.controlTypeId === ControlType.RADIOBUTTON && (
                <fieldset id={props?.label} className={style["af-radio-button"]}>
                    <legend>{props.label}</legend>
                    {question?.availableQuestionAnswers.map((opt, idx) => (
                        <div
                            className={style["checklist-radio-button"]}
                            key={`${idx}${question?.id}`}>
                            <input
                                type="radio"
                                id={`${props?.label} ${opt.displayText}`}
                                name={`${props?.label} ${opt.displayText}`}
                                value={opt.value}
                                checked={value === opt.value}
                                onChange={(e) => {
                                    handleInputTextValue(e.target.value);
                                }}
                            />
                            <label
                                className={style["radion-button--label-container"]}
                                htmlFor={`${props?.label} ${opt.displayText}`}>
                                <p className={style["rb--label"]}>{opt.displayText}</p>
                                <div className={style["rb--checkicon-container"]}>
                                    <div className={style["rb--checkicon"]}></div>
                                </div>
                            </label>
                        </div>
                    ))}
                </fieldset>
            )}
            {question.controlTypeId === ControlType.DATE_DROPDOWN && (
                <AFDateDropdown
                    question={question}
                    availableQuestionAnswers={question?.availableQuestionAnswers}
                    answerChanges={answerChanges}
                />
            )}
            {question.controlTypeId === ControlType.DATE_PICKER && (
                <AFDateInput
                    question={question}
                    answerChanges={answerChanges}
                    inputRef={handleElementRef}
                />
            )}
            {question.controlTypeId === ControlType.DROPLIST && (
                <div className={style["af-dropdown"]}>
                    <Dropdown
                        id={props?.label}
                        name={props?.label}
                        options={question?.optionList}
                        selectedOptionId={valueDropList}
                        onChange={(e) => {
                            handleDropListValue(e.target.value);
                        }}
                        variant={"default"}
                        className={style["af-dropdown--style"]}
                    />
                </div>
            )}
            {question.controlTypeId === ControlType.OPENTEXT_EXTRA && (
                <AFTextareaField
                    question={question}
                    answerChanges={answerChanges}
                    label={props?.label}
                />
            )}
            {question.controlTypeId === ControlType.SINGLE_TEXT && (
                <AFSingleText
                    question={question}
                />
            )}
            {question.controlTypeId === ControlType.BANK_ACCOUNT && (
                <AFBankAccountInput
                    question={question}
                    answerChanges={answerChanges}
                    textRef={handleElementRef}
                />
            )}
            {question.controlTypeId === ControlType.ADDRESSFIELD && showAddress && (
                <AFAddressField
                    question={question}
                    answerChanges={answerChanges}
                    label={props?.label}
                    apiKey={googlePlacesApiKey}
                />
            )}
            {question.controlTypeId === ControlType.EMAIL_ADDRESS && (
                <div className={style["af-text-input"]} id={`${question.id}`}>
                    <div className={cx([style["input-field"]])}>
                        <Input
                            id={props?.label}
                            name={props?.label}
                            type="email"
                            value={email || ""}
                            onChange={(e) => {
                                handleEmailValue(e.target.value);
                            }}
                            placeholder={question?.placeholder || ""}
                            textRef={handleElementRef}></Input>
                    </div>
                </div>
            )}
            {question.controlTypeId === ControlType.PHONE_NUMBER && (
                <div className={style["af-text-input"]} id={`${question.id}`}>
                    <div className={cx([style["input-field"]])}>
                        <Input
                            id={props?.label}
                            name={props?.label}
                            type="text"
                            value={value || ""}
                            onChange={(e) => {
                                handleInputTextValue(
                                    e.target.value.replace(/[^0-9\-()]/g, "")
                                );
                            }}
                            placeholder={question?.placeholder || ""}
                            textRef={handleElementRef}></Input>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default AppFormField;
